import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import { BehaviorSubject, ReplaySubject, Subscription } from 'rxjs';

import { MtnaPopoverConfig } from '@mtna/lib-ui';
import { Variable, VariableSummary } from '@mtna/pojo-consumer-ui';

import { BaseFilter } from '../core/models/filter/base-filter.model';
import { RdsVariableUtil } from '../core/utils/variable-util';
import { ALL_MEASURE_OPTION_TYPES } from '../measure-select/measure-option.model';
import { MeasureSelection } from '../measure-select/measure-selection.model';

export interface RdsTabulationDimensionsForm<V extends Variable | VariableSummary = Variable> {
  columns?: Array<V>;
  filters?: Array<BaseFilter<V>>;
  rows: Array<V>;
  weights?: Array<V>;
  measures?: MeasureSelection<V>[];
}

export const DEFAULT_RDS_MEASURES = [new MeasureSelection<Variable | VariableSummary>('COUNT')];

@Component({
  selector: 'rds-dimension-selection',
  templateUrl: './dimension-selection.component.html',
  styleUrls: ['./dimension-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsDimensionSelectionComponent implements OnDestroy, OnInit {
  /** Selected column dimensions */
  @Input()
  get columns(): Array<VariableSummary> {
    return this._columns;
  }
  set columns(columns: Array<VariableSummary>) {
    this._columns = columns;
    if (!!this.form) {
      // If the form has been initialized and the columns are set,
      // update the form value
      this.form
        .get('columns')
        .setValue(!!this.columns ? this.columns.filter((c) => this.variables.some((v) => RdsVariableUtil.compareVariables(v, c))) : null);
    }
    if (!!this.columns && this.columns.length) {
      this.disabledRowOptions$.next(this.columns);
    }
  }
  private _columns: Array<VariableSummary> = [];

  /** Popover config for column selection */
  @Input() columnsConfig = new MtnaPopoverConfig(
    'Columns',
    'Categorical variables that will act as column dimensions in the tabulation table.'
  );

  /**
   * NgStyle config for the dialog content's filter component, defaults to `{ height: '300px' }`
   * @example
   * { 'maxHeight': '300px' }
   */
  @Input() dialogFilterStyles: { [key: string]: string } = { height: '300px' };
  /** Optional filter dialog min-width */
  @Input() dialogMinWidth: string;

  /** Optional filter */
  @Input() get filters(): Array<BaseFilter<Variable | VariableSummary>> {
    return this._filters;
  }
  set filters(filters: Array<BaseFilter<Variable | VariableSummary>>) {
    this._filters = filters;
    if (!!this.form) {
      // If the form has been initialized and the filter is set,
      // update the form value
      this.form
        .get('filters')
        .setValue(
          !!this.filters &&
            !!this.filters.every((f) => !!f.variable && this.variables.some((v) => RdsVariableUtil.compareVariables(v, f.variable)))
            ? [...this.filters]
            : null
        );
    }
  }

  private _filters: Array<BaseFilter<Variable | VariableSummary>>;

  /** Popover config for filter select */
  @Input() filterConfig: MtnaPopoverConfig = new MtnaPopoverConfig(
    'Filter',
    'Filters the data on any available variable (logically joined using the AND operator if more than one is specified).',
    'after'
  );
  /** Maximum number of column dimensions */
  @Input() maxColumns = 1;
  /** Maximum number of row dimensions */
  @Input() maxRows = 1;

  /** Selected meausre */
  @Input()
  get measures(): MeasureSelection<Variable | VariableSummary>[] {
    return this._measures;
  }
  set measures(measures: MeasureSelection<Variable | VariableSummary>[]) {
    this._measures = measures;
    if (!!this.form && !!this.measureVariables) {
      // If the form has been initialized and the measures are set,
      // update the form value
      this.form.get('measures').setValue(this.measures);
    }
  }
  private _measures: MeasureSelection<Variable | VariableSummary>[] = DEFAULT_RDS_MEASURES;

  /** Popover config for measure select */
  @Input() measureConfig: MtnaPopoverConfig = new MtnaPopoverConfig(
    'Measure',
    'Count/Percentage of records, or statistical aggregation (e.g. sum, average) of numeric variables, rolled up by dimensions, that define the content of the table cell values.',
    'after'
  );

  /** Optional array of measure variables to choose from */
  @Input()
  get measureVariables(): Array<Variable | VariableSummary> | null {
    return this._measureVariables;
  }
  set measureVariables(variables: Array<Variable | VariableSummary> | null) {
    this._measureVariables = variables;
    if (!!this.form && !!this.measureVariables && !!this.measures) {
      this.form.get('measures').setValue(this.measures);
    }
  }
  private _measureVariables: Array<Variable | VariableSummary> | null;

  /** Whether the user can select multiple filter codes */
  @Input() multiFilterCodes = false;
  /** Whether the user can select multiple filter variables */
  @Input() multiFilterVariables = false;
  /** Selected row dimensions */
  @Input()
  get rows(): Array<VariableSummary> {
    return this._rows;
  }
  set rows(rows: Array<VariableSummary>) {
    this._rows = rows;
    if (!!this.form) {
      // If the form has been initialized and the rows are set,
      // update the form value
      this.form
        .get('rows')
        .setValue(!!this.rows ? this.rows.filter((c) => this.variables.some((v) => RdsVariableUtil.compareVariables(v, c))) : null);
    }
    if (!!this.rows && this.rows.length) {
      this.disabledColumnOptions$.next(this.rows);
    }
  }
  private _rows: Array<VariableSummary> = [];

  /** Popover config for row selecttion */
  @Input() rowsConfig = new MtnaPopoverConfig(
    'Rows',
    'Categorical variables that will act as row dimensions in the tabulation table. A minimum of one row is required to tabulate.'
  );

  /** Whether currently tabulating */
  @Input()
  get tabulating(): boolean {
    return this._tabulating;
  }
  set tabulating(tabulating: boolean) {
    this._tabulating = tabulating;
    if (tabulating && !!this.form) {
      this.form.disable();
    } else if (!tabulating && !!this.form) {
      this.form.enable();
    }
  }
  private _tabulating: boolean;

  /** List of variables available to select */
  @Input() get variables(): Array<VariableSummary> {
    return this._variables;
  }
  set variables(variables: Array<VariableSummary>) {
    this._variables = variables;
    if (!!this.form && !!this.variables) {
      // When variables change, form is reset to a default state
      const defaultState: RdsTabulationDimensionsForm = {
        filters:
          !!this.filters &&
          this.filters.every((f) => !!f.variable && this.variables.some((v) => RdsVariableUtil.compareVariables(v, f.variable)))
            ? [...this.filters]
            : null,
        rows: !!this.rows ? this.rows.filter((r) => this.variables.some((v) => RdsVariableUtil.compareVariables(v, r))) : null,
        columns: !!this.columns ? this.columns.filter((c) => this.variables.some((v) => RdsVariableUtil.compareVariables(v, c))) : null,
        weights: !!this.weights
          ? this.weights.filter((w) => this.weightedVariables.some((v) => RdsVariableUtil.compareVariables(v, w)))
          : null,
        measures: !!this.measures ? this.measures : null,
      };
      // Reset the form using the default state values
      this.form.reset(defaultState);
    }
  }
  private _variables: Array<VariableSummary>;

  /** Selected variable weights */
  @Input()
  get weights(): Array<VariableSummary> {
    return this._weights;
  }
  set weights(weights: Array<VariableSummary>) {
    this._weights = weights;
    if (!!this.form && !!this.weightedVariables) {
      // If the form has been initialized and the weights are set,
      // update the form value
      this.form
        .get('weights')
        .setValue(
          !!this.weights ? this.weights.filter((w) => this.weightedVariables.some((v) => RdsVariableUtil.compareVariables(v, w))) : null
        );
    }
  }
  private _weights: Array<VariableSummary> = [];

  /** Popover config for weight select */
  @Input() weightsConfig: MtnaPopoverConfig = new MtnaPopoverConfig('Weighted Variables', 'Add variables to weight the tabulation');
  /** Optional array of weighted variables to choose from */
  @Input()
  get weightedVariables(): Array<Variable | VariableSummary> | null {
    return this._weightedVariables;
  }
  set weightedVariables(variables: Array<Variable | VariableSummary> | null) {
    this._weightedVariables = variables;
    if (!!this.form && !!this.weightedVariables && this.weights) {
      this.form
        .get('weights')
        .setValue(
          !!this.weights ? this.weights.filter((w) => this.weightedVariables.some((v) => RdsVariableUtil.compareVariables(v, w))) : null
        );
    }
  }
  private _weightedVariables: Array<Variable | VariableSummary> | null;

  /** The selected TabulationDimensions to be tabulated */
  @Output() tabulate = new EventEmitter<RdsTabulationDimensionsForm>();

  disabledColumnOptions$ = new BehaviorSubject<VariableSummary[]>([]);
  disabledRowOptions$ = new BehaviorSubject<VariableSummary[]>([]);
  form: FormGroup;
  formValueChangeSub = new Subscription();
  /**
   * Subject that emits the latest selected variables accross all dimension controls.
   * Used to prevent variables from being selected multiple times.
   */
  selectedVariables$ = new ReplaySubject<string[]>(1);

  _allMeasureTypes = [...ALL_MEASURE_OPTION_TYPES];
  _countAndPercentMeasureTypes = ['COUNT', 'PERCENT'];

  constructor(private cdr: ChangeDetectorRef, private fb: FormBuilder) {}

  ngOnDestroy(): void {
    this.formValueChangeSub.unsubscribe();
  }

  ngOnInit() {
    const columnValidators = this.maxColumns > 0 ? [maximumSelection(this.maxColumns)] : null;
    const rowValidators = [maximumSelection(this.maxRows), Validators.required];
    this.form = this.fb.group({
      columns: this.fb.control(!!this.columns ? [...this.columns] : null, columnValidators),
      rows: this.fb.control(!!this.rows ? [...this.rows] : null, rowValidators),
      filters: this.fb.control(!!this.filters ? [...this.filters] : null),
      weights: this.fb.control(!!this.weights ? [...this.weights] : null),
      measures: this.fb.control(this.measures),
    });

    // Disable the form if tabulating
    if (this.tabulating) {
      this.form.disable();
    }

    // Initial disabled
    if (!!this.rows && this.rows.length) {
      this.disabledColumnOptions$.next(this.rows);
    }
    if (!!this.columns && this.columns.length) {
      this.disabledRowOptions$.next(this.columns);
    }

    // emit initial selected values
    this._emitSelectedVariables(this.form.value);
    // each time the form changes, emit selected variables
    this.formValueChangeSub.add(this.form.valueChanges.subscribe((valueChanges) => this._emitSelectedVariables(valueChanges)));
  }

  submitForm() {
    if (this.form.valid) {
      this.tabulate.emit(this.form.value);
    }
  }

  /**
   * Get all the selected variable IDs and put them in the selected array
   * to disable these from being selected multiple times.
   * exclude filter and weights
   */
  private _emitSelectedVariables(valueChanges: any) {
    const newSelections = new Array<string>();
    for (const prop in valueChanges) {
      if (valueChanges.hasOwnProperty(prop) && prop !== 'filter' && prop !== 'weights' && !!valueChanges[prop]) {
        newSelections.push(valueChanges[prop].id);
      }
    }
    this.selectedVariables$.next(newSelections);
    this.cdr.markForCheck();
  }
}

/**
 * Function for validating max selections limits
 * @param limit upper limit to check against
 */
function maximumSelection(limit = 1): ValidatorFn {
  const validator: ValidatorFn = (ctrl: AbstractControl) => {
    const totalSelected = !!ctrl.value ? ctrl.value.length : 0;
    return totalSelected <= limit ? null : { maxSelected: `Select no more than ${limit}` };
  };
  return validator;
}
